import { VariantProps, cva } from 'class-variance-authority'
import React from 'react'

import { cn } from '@/ui/utils/style'

export type BaseElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span'

const typographyVariants = cva('text-foreground', {
  variants: {
    variant: {
      h1: 'font-bold text-4xl leading-none lg:text-5.5xl',
      h2: 'font-bold text-4xl leading-none',
      h3: 'font-bold text-2xl leading-none',
      h4: 'font-normal text-base leading-none',
      h5: 'font-normal text-xl leading-none',
      p: 'font-normal text-base',
      span: 'font-normal text-base',
      prompt: 'text-muted-foreground text-xs leading-none tracking-tight',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
})

interface TypographyProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof typographyVariants> {
  element?: BaseElement
  /** Applies the yellow/green text gradient treatment. */
  gradient?: boolean
  /**
   * Opt out of gradient shrink/layout defaults (`width: fit-content`, `align-self: flex-start`).
   * Use this when the text should follow parent flex alignment (e.g. vertically centered rows).
   */
  gradientLayoutReset?: boolean
}

function variantToElement(variant: VariantProps<typeof typographyVariants>['variant']): BaseElement {
  if (variant === 'prompt') return 'span'
  return variant ?? 'p'
}

const gradientStyle: React.CSSProperties = {
  background: 'linear-gradient(to right, #FBE572, #C2F4BC)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  // Descenders (g, y, p) can clip with tight line-heights; scale buffer with font size.
  paddingBottom: '0.12em',
  display: 'inline-block',
  width: 'fit-content',
  alignSelf: 'flex-start',
}

const gradientLayoutResetStyle: React.CSSProperties = {
  width: 'auto',
  alignSelf: 'auto',
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ element, variant, gradient, gradientLayoutReset, className, style, ...props }, ref) => {
    const Element = element ?? variantToElement(variant)

    return (
      <Element
        className={cn(typographyVariants({ variant, className }))}
        style={
          gradient
            ? { ...gradientStyle, ...(gradientLayoutReset ? gradientLayoutResetStyle : undefined), ...style }
            : style
        }
        ref={ref as any}
        {...props}
      />
    )
  },
)
Typography.displayName = 'Typography'

export { Typography, type TypographyProps }
